import { Select as SelectPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { fieldClasses } from './field-styles';
import { Icon } from './icon';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Rendered before the label in the list, e.g. a colour dot. The trigger shows `leading`. */
  leading?: ReactNode;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  /** Rendered before the label in the trigger, e.g. a colour dot. */
  leading?: ReactNode;
}

export function Select({
  options,
  placeholder,
  className,
  id,
  leading,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  ...root
}: SelectProps) {
  return (
    <SelectPrimitive.Root {...root}>
      <SelectPrimitive.Trigger
        id={id}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        className={cn(
          fieldClasses,
          'flex h-control cursor-pointer items-center gap-2 px-3 text-left data-placeholder:text-fg-subtle',
          className,
        )}
      >
        {leading}
        <span className="min-w-0 flex-1 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <Icon name="chevrons-up-down" className="text-fg-subtle" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border border-line bg-raised shadow-overlay data-[state=closed]:animate-fade-out data-[state=open]:animate-pop-in"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="relative flex min-h-control cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2.5 outline-none select-none data-disabled:cursor-not-allowed data-disabled:text-fg-disabled data-highlighted:bg-hover"
              >
                {option.leading}
                <div className="min-w-0">
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  {option.description && (
                    <div className="text-xs text-fg-subtle">{option.description}</div>
                  )}
                </div>
                <SelectPrimitive.ItemIndicator className="absolute right-2.5">
                  <Icon name="check" className="text-brand" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
